import { Fragment, useEffect, useMemo, useState } from 'react';
import {useTranslation} from 'react-i18next';
import {BllinkLogger} from '../../../common/utils/bllink_loggers';
import { constants } from "../../../common/constants/constants";
import {StyledSection} from './yearlyByMonth.styled';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import Search from '../../../images/search.svg';
import Select from 'react-select';
import {selectCustomStyles} from '../../../common/shared/select.styled';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import Table from '../../../common/table/TableFunctional';
import {useDispatch, useSelector} from 'react-redux';
import {getYearsOptions, shortenText} from '../../../common/shared/statelessMethods';
import {hideLoader, showLoader} from '../../../store/common/slice';
import {fetchPaymentsByYear} from '../../../store/buildings/actions';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import MonthTableLogic from '../table/monthTableLogic';
import FullPaid from '../../../images/full_paid.svg';
import HalfPaid from '../../../images/half_checked.svg';
import ScheduledToPay from '../../../images/managers/scheduled_to_pay.png';
import TenantRecord from '../../../images/tenant_claim_paid.svg';
import {useAccessToken} from '../../../common/hooks/useAccessToken';
import { createTotalRow, createPercentageRow } from '../../../common/utils/createTotalRows';
import { isAdminSelector } from '../../../store/auth/selectors';
import EditCell from './EditCell';
import BllinkUtils from '../../../common/utils/utils';
import {StyledActionsBar} from '../report.styled';
import {Currency} from "../../../common/currency";
import {OneTimeIcon} from "./oneTimeIcon";
import { PageTitle } from '../../../common/PageTitle';
import { StyledPdfWrapper } from "../debtList/PdfWrapper.styled";

export default function YearlyByMonthTable({history, match, isOneTime}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const userCheck = useSelector((state) => state.auth.userCheck);
    const client = useAccessToken();
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const {buildingID, year} = match.params;
    const yearOptions = getYearsOptions();

    const [filters, setFilters] = useState({});
    const [rows, setRows] = useState([]);
    const [pdfTableHeader, setPdfTableHeader] = useState([]);
    const [oneTimeHeaders, setOneTimeHeaders] = useState([]);
    const [selectedYear, setSelectedYear] = useState({
        label: year,
        value: parseInt(year)
    });

    document.title = t('titles.yearly_report');
    useBodyClass(['yearly_by_months', 'report']);

    useEffect(async () => {
        if (client != null) {
            dispatch(showLoader());
            await loadPayments(selectedYear.value);
            dispatch(hideLoader());
        }
    }, [client]);


    const getOneTimePdfHeaderRow = (oneTimeHeaders) => {
        const oneTimeHeaderRow = [{
            apartmentNumber: t('reports.apartmentName'),
            tenantName: t('reports.tenantName'),
        }]
        oneTimeHeaders.forEach(header => {
            oneTimeHeaderRow[0][`one_time_${header.id.toString()}`] = header.name
        })
        return oneTimeHeaderRow;
    }


    const getComputedPdfRows = (raws, pdfHeaders) => {
        if (!raws.length || !pdfHeaders[0]) return [];
        return raws.map(row => {
            const keys = Object.keys(pdfHeaders[0]);
            const rowData = {}
            keys.forEach(key => {
                if (!!row[key]) {
                    rowData[key] = typeof row[key] === 'object' ? row[key]?.paid?.value : row[key]
                }
            })
            return rowData;
        });
    }

    const onYearChanged = async year => {
        setSelectedYear(year);
        const newUrl = `/${isOneTime ? constants.pages.yearlyByOneTimeUrl : constants.pages.yearlyByMonthsUrl}/${buildingID}/${year.value}`;
        history.push({pathname: newUrl});
        await loadPayments(year.value);
    }

    const loadPayments = async year => {
        dispatch(showLoader());

        const {payload} = await dispatch(fetchPaymentsByYear({
            buildingID,
            year
        }));

        if (payload?.apartments) {
            let {rows: unsorted, headerExtras} = payload.apartments;
            const rows = [...unsorted]
                .sort((a,b) => parseInt(a.apartmentNumber) <  parseInt(b.apartmentNumber) ? -1 : 1)
                .map(r => ({...r}));  // need clone as we modify this data but data is immutable
            for (const row of rows) {
                const tenantNames = row['tenantName'];
                // NOTE: tenantNames initially was a string, then it was changed to array,
                //       to avoid regressions due to cached data we checking it here
                row['tenantName'] = !Array.isArray(tenantNames) ? tenantNames : (tenantNames
                    .map(({ tenantName: tn, tenantType: tt }) => ({ name: tn ?? '', type: tt === 'unknown' ? null : t(`reports.tenantType.${tt}`) }))
                    .map(r => r.type == null ? r.name : `${r.name} (${r.type})`)
                    .join(', '));
            }
            BllinkLogger.info(`loadYear rows after sorted are `, rows);
            const oneTimeTableHeaders = headerExtras.filter(item => item.id);
            setOneTimeHeaders(oneTimeTableHeaders);
            const pdfHeaders = getOneTimePdfHeaderRow(oneTimeTableHeaders);
            setPdfTableHeader(pdfHeaders);
            if (rows?.length > 0) {
                const currency = rows[0].total?.charged?.currency || constants.defaultCurrency;
                const totalRow = createTotalRow(rows, currency);
                totalRow['tenantName'] = t('reports.total');
                const percentageRow = createPercentageRow(totalRow, currency);
                percentageRow['tenantName'] = t('reports.percentage');
                setRows([...rows, totalRow, percentageRow]);
            }
        }

        dispatch(hideLoader());
    }

    const pdfRows = useMemo(() => {
        return getComputedPdfRows(rows, pdfTableHeader)
    }, [rows, pdfTableHeader]);

    const checkOneTimeData = () => {
        return rows?.some(row => Object.keys(row).some(key => key.match(/one_time_/g)))
    }

    const columnsMonth = useMemo(
        () => {
            const stableDataColumns = [
                {
                    Header: t('reports.apartmentName'),
                    accessor: 'apartmentName',
                    Cell: ({value}) => (
                        <a href={`/${constants.pages.newApartmentReport}/${buildingID}/${value}`} rel="noreferrer" target='_blank'>{value}</a>
                    ),
                },
                {
                    Header: t('reports.tenantName'),
                    Cell: ({value}) =>  {
                        let text = shortenText(value);
                        return (
                            <OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {text?.originalComment}
                                    </Tooltip>
                                )}
                            >
                                <span exceltext={text?.originalComment || text}>{text?.shortComment || text}</span>
                            </OverlayTrigger>
                        );
                    },
                    accessor: 'tenantName',
                }
            ];

            const monthColumns = constants.months.map(monthInt => {
                const label = t(`common.months.${monthInt}`);
                return {
                    id: label,
                    Header: () => (<a href={`/${constants.pages.monthlyCashFlowUrl}/${buildingID}/${monthInt}/${selectedYear.value}`}>{label}</a>),
                    Cell: ({row}) => MonthTableLogic.renderIcon(row.original[constants.MonthsToText[monthInt]], {
                        tableType: 'yearlyByMonths'
                    }, row.original.tenantName),
                    accessor: (row) => row[constants.MonthsToText[monthInt]] && row[constants.MonthsToText[monthInt]].paid.value,
                }
            });

            const totalColumns = [
                {
                    Header: t('reports.total_charged'),
                    accessor: 'total.charged.value',
                    Cell: ({row, value}) => row?.original?.total ? (<span exceltext={value}>{BllinkUtils.Strings.numberWithCommas(value)}<Currency>{row.original.total.charged.currency}</Currency></span>) : '',
                },
                {
                    Header: t('reports.total_debt_col'),
                    accessor: 'total.debt.value',
                    Cell: ({row, value}) => row?.original?.total ? (<span exceltext={value}>{BllinkUtils.Strings.numberWithCommas(value)}<Currency>{row.original.total.debt.currency}</Currency></span>) : '',
                },
                {
                    Header: t('reports.total_future'),
                    accessor: 'total.future.value',
                    Cell: ({row, value}) => row?.original?.total ? (<span exceltext={value}>{BllinkUtils.Strings.numberWithCommas(value)}<Currency>{row.original.total.future.currency}</Currency></span>) : '',
                },
            ];

            const additionalColumns = [
                {
                    Header:'',
                    id: 'edit',
                    accessor: 'edit',
                    Cell: ({ row }) => <EditCell row={row.original} buildingID={buildingID} year={year} selectedYear={selectedYear} />
                },
            ];

            return stableDataColumns.concat(monthColumns).concat(totalColumns).concat(additionalColumns);
        },
        [rows]
    );

    const columnsOneTime = useMemo(
        () => {
            const stableDataColumns = [
                {
                    Header: t('reports.apartmentName'),
                    accessor: 'apartmentName',
                    Cell: ({value}) => (
                        <a href={`/${constants.pages.newApartmentReport}/${buildingID}/${value}`} rel="noreferrer" target='_blank'>{value}</a>
                    ),
                },
                {
                    Header: t('reports.tenantName'),
                    accessor: 'tenantName',
                }
            ];

            const oneTimeColumns = oneTimeHeaders.map(header => ({
                id: header.id,
                Header: header.name,
                Cell: ({row}) => (<OneTimeIcon oneTimeData={row.original[`one_time_${header.id}`]} />),
                accessor: (row) => row[`one_time_${header.id}`] && row[`one_time_${header.id}`].paid.value,
            }));

            return stableDataColumns.concat(oneTimeColumns);
        },
        [rows, oneTimeHeaders]
    );

    const tableData = useMemo(() => rows, [rows, oneTimeHeaders]);

    if (!userCheck) {
        return (<div className="div-buffer-empty-content" />);
    }

    const renderExplanation = () => {
        return (
            <div className="explanation-section">
                <ul className="explanation-section-list">
                    <li>
                        <img alt="all paid" src={FullPaid}/>
                        {t('reports.paid_explanation')}
                    </li>
                    <li>
                        <img alt="half paid" src={HalfPaid}/>
                        {t('reports.part_paid_explanation')}
                    </li>
                    <li>
                        <img alt="none paid" src={ScheduledToPay}/>
                        {t('reports.not_paid_explanation')}
                    </li>
                    <li>
                        <img alt="none paid" src={TenantRecord}/>
                        {t('reports.tenant_claim_paid_explanation')}
                    </li>
                </ul>
            </div>
        );
    }

    const tableTitle = useMemo(() => {
        return t(isOneTime ? 'reports.yearly_by_months_one_time_title' : 'reports.yearly_by_months_title', { year: selectedYear.value ?? '' });
    }, [isOneTime, selectedYear]);

    const printTablesOneTime = useMemo(() => {
        if (columnsOneTime?.length) {
            const headers = columnsOneTime.slice(0, 2);
            const payments = columnsOneTime.slice(2);
            const tablesCount = Math.ceil(payments.length / 8);
            const tables = Array(tablesCount);
            for (let i = 0; i < tablesCount; i++) {
                tables[i] = headers.concat(payments.slice(i * 8, (i + 1) * 8));
            }
            return tables;
        }
    }, [columnsOneTime])

    const printTablesMonths = useMemo(() => {
        return columnsMonth.slice(0, -1);
    }, [columnsMonth])

    const getPrintTableColumnData = (accessor, data) => {
        if (!data || !accessor) return '';
        if (typeof accessor === 'string') {
            return data[accessor];
        } else if (typeof accessor === 'function') {
            return accessor(data);
        }
    }

    const getMonthsHeaderData = (header) => {
        if (!header) return '';
        if (typeof header === 'string') {
            return header;
        } else if (typeof header === 'function') {
            return header();
        }
    }

    const getTotalMonthsValues = (currency, value) => {
        if (!value && value !== 0) return '';
        return <span>{BllinkUtils.Strings.numberWithCommas(value)}<Currency>{currency}</Currency></span>;
    }

    return (
        <StyledSection>
            <PageTitle>
                {isOneTime && !checkOneTimeData() ? '' : (
                    <PrintPdfDropdown
                        buildingID={buildingID}
                        eventLabel="yearlyByMonth"
                        titleString={`${selectedYear.value} ${t(isOneTime ? 'reports.yearly_by_months_one_time_title' : 'reports.yearly_by_months_title', {year: selectedYear.value ?? ''})}`}
                        excelTitle={`${selectedYear.value} ${t(isOneTime ? 'reports.yearly_by_months_one_time_title' : 'reports.yearly_by_months_title', {year: selectedYear.value ?? ''})}`}
                        reportName={t('reports.special_payments_report_title')}
                        table={true}
                        pdfIframe={isOneTime}
                        titleRow={isOneTime ? pdfTableHeader : []}
                        rows={pdfRows}
                        pdfSettings={{chunkSize: 10, orientation: constants.pages.pdfOrientation.landscape}}
                        disablePdf={false}
                    />
                )}
            </PageTitle>

            <StyledActionsBar className={'actions-bar'}>
                <div className="control-with-icon search actions-bar-search">
                    <input
                        type="text"
                        className="form-control bllink-input-border"
                        name="search"
                        onChange={e => setFilters({
                            global: e.target.value
                        })}
                    />
                    <img src={Search} alt={t('search.search')} />
                </div>
                <div className='actions-bar-select'>
                    <Select
                        styles={selectCustomStyles}
                        options={yearOptions}
                        value={selectedYear}
                        onChange={onYearChanged}
                    />
                </div>
            </StyledActionsBar>

            {
                isOneTime && !checkOneTimeData() ? (
                    <h2 className="text-center">
                        {t('reports.empty_table_for', { name: `${selectedYear.value} ${t('reports.yearly_by_one_time_title')}` })}
                    </h2>
                ) : (
                    <>
                        {tableData && tableData.length > 0 && (
                            <Fragment>
                                <div>
                                    <Table
                                        buildingId={buildingID}
                                        columns={isOneTime ? columnsOneTime : columnsMonth}
                                        data={tableData}
                                        filters={filters}
                                        tableTitle={tableTitle}
                                        hiddenColumns={isAdmin ? [] : ['edit']}
                                    />
                                    {renderExplanation()}
                                </div>
                                <div id="section-to-print">
                                    <StyledPdfWrapper ltr={ltr}>
                                        <div className='pdf-title'>{tableTitle}</div>


                                        {isOneTime ? printTablesOneTime.map(table => {
                                            return (
                                                <div className='pdf-rows-wrapper pdf-rows-table'>
                                                    <div className='pdf-row'>
                                                        {table.map(column => <div className="print-column-one-time" key={column.Header}>{column.Header}</div>)}
                                                    </div>
                                                    {tableData?.length ? tableData.map(data => {
                                                        return <div className='pdf-row'>
                                                            {table.map((column, i) => <div className="print-column-one-time" key={i}>{getPrintTableColumnData(column.accessor, data)}</div>)}
                                                        </div>
                                                    }) : null}
                                                </div>
                                            )
                                        }) : <div className='pdf-rows-wrapper pdf-rows-table months-print-table'>
                                                <div className='pdf-row'>
                                                    {printTablesMonths.map((months, i) => (<div className="print-column-months vertical-text" key={i}>
                                                        {getMonthsHeaderData(months.Header)}
                                                    </div>))}
                                                </div>
                                                {tableData?.length ? tableData.map(data => {
                                                    return <div className='pdf-row'>
                                                        {printTablesMonths.slice(0, -3).map((column, i) => <div className="print-column-months" key={i}>
                                                            {getPrintTableColumnData(column.accessor, data)}
                                                        </div>)}
                                                        <div className="print-column-months">
                                                            {getTotalMonthsValues(data?.total?.charged?.currency, data?.total?.charged?.value)}
                                                        </div>
                                                        <div className="print-column-months">
                                                            {getTotalMonthsValues(data?.total?.debt?.currency, data?.total?.debt?.value)}
                                                        </div>
                                                        <div className="print-column-months">
                                                            {getTotalMonthsValues(data?.total?.future?.currency, data?.total?.future?.value)}
                                                        </div>
                                                    </div>
                                                }) : null}
                                            </div>
                                        }
                                    </StyledPdfWrapper>
                                </div>
                            </Fragment>
                        )}
                    </>
                )
            }
        </StyledSection>
    );
}
